import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, FileText, FolderKanban, Newspaper, Image, Images, Presentation, Sparkles, Users, Award, IdCard, Mail, Heart, Settings2, ScrollText, LogOut, HeartHandshake, Globe, Layers, Navigation as NavIcon, FolderOpen, Search as SeoIcon, UserCog } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Toaster } from "../../components/ui/sonner";

const MENU = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/content", icon: Settings2, label: "Site Content & Settings" },
  { to: "/admin/hero", icon: Presentation, label: "Home Hero" },
  { to: "/admin/collections/work-areas", icon: Layers, label: "Our Work" },
  { to: "/admin/collections/projects", icon: FolderKanban, label: "Projects" },
  { to: "/admin/collections/articles", icon: FileText, label: "Articles" },
  { to: "/admin/collections/news", icon: Newspaper, label: "News & Events" },
  { to: "/admin/collections/gallery", icon: Image, label: "Gallery" },
  { to: "/admin/albums", icon: Images, label: "Gallery Albums" },
  { to: "/superadmin/greetings", icon: Sparkles, label: "Greeting Studio" },
  { to: "/admin/navigation", icon: NavIcon, label: "Navigation" },
  { to: "/admin/media", icon: FolderOpen, label: "Media Library" },
  { to: "/admin/seo", icon: SeoIcon, label: "SEO" },
  { to: "/admin/participants", icon: Users, label: "Participants" },
  { to: "/admin/participants?tab=certificates", icon: Award, label: "Certificates" },
  { to: "/admin/participants?tab=idcards", icon: IdCard, label: "ID Cards" },
  { to: "/admin/enquiries", icon: Mail, label: "Enquiries" },
  { to: "/admin/donations", icon: Heart, label: "Donations" },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/account", icon: UserCog, label: "My Account" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Logs" },
];

export default function AdminLayout() {
  const { user, authChecked, logout } = useApp();
  const navigate = useNavigate();

  if (!authChecked) return <div className="flex min-h-screen items-center justify-center bg-navy-50 text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/superadmin" replace />;

  const isAdmin = user.role === "admin";
  const menu = isAdmin ? MENU : MENU.filter((m) => ["/superadmin/greetings", "/admin/account"].includes(m.to));

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-navy lg:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-saffron-500 text-navy"><HeartHandshake size={18} /></span>
          <div className="leading-tight">
            <p className="font-display text-sm font-bold text-white">Sahaj Sarathi</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{isAdmin ? "Super Admin" : "Greeting Admin"}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" aria-label="Admin">
          {menu.map((m) => (
            <NavLink key={m.label} to={m.to} end={m.end} data-testid={`admin-nav-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-saffron-500 text-navy" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
              <m.icon size={17} strokeWidth={1.8} /> {m.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <Link to="/" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10" data-testid="admin-view-site"><Globe size={16} /> View Website</Link>
          <button onClick={async () => { await logout(); navigate("/superadmin"); }} data-testid="admin-logout"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10 hover:text-red-300">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3 lg:hidden">
          <span className="font-display text-sm font-bold text-navy">SSF Admin</span>
          <div className="flex gap-2">
            <Link to="/" className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy">Site</Link>
            <button onClick={async () => { await logout(); navigate("/superadmin"); }} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-red-500" data-testid="admin-logout-mobile">Logout</button>
          </div>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-gray-200 bg-white px-4 py-2 lg:hidden" aria-label="Admin mobile">
          {menu.map((m) => (
            <NavLink key={m.label} to={m.to} end={m.end}
              className={({ isActive }) => `whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold ${isActive ? "bg-navy text-white" : "text-slate-500"}`}>
              {m.label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
